/*
  Peakside Performance — Tune Builder wizard
  ------------------------------------------
  Multi-step configurator: truck → device → tune → add-ons → order form → cart.
  Catalog routes below map engine selections to store products by handle.
  Prices/titles are fetched live from /products/<handle>.js so the wizard
  stays current when products change; handles only need updating when
  products are replaced.
*/
(function () {
  'use strict';

  /* ============================== CATALOG ============================== */

  var PRODUCTS = {
    // devices
    autocal: 'efilive-blank-ac3-autocal',
    ezlynk: 'ez-lynk-auto-agent-3-0',
    mpvi4: 'hp-tuners-blank-mpvi3-m03-000-00',
    rtd4: 'hp-tuners-rtd4-remote-tuning-device',
    sctx4: '1996-2019-powerstroke-sct-x4-powerflash-7015',
    livewire: '1996-2019-powerstroke-livewire-ts',
    amdpProgrammer: '2020-2022-powerstroke-amdp-programmer-no-tune',
    // licensing / credits
    efiliveLicense: 'efi-live-autocal-flashscan-vin-license',
    hptCredits: 'universal-hp-tuners-credits',
    // cables / connectors
    cumminsPatchCable: '2018-cummins-ez-lynk-3-0-obdii-patch-cable',
    cumminsBypassCable: '2018-2024-cummins-bypass-cable',
    cumminsDiagCable: '2007-5-2025-cummins-ez-lynk-autoagent-2-3-diagnostics',
    canbus1722: '2017-2022-duramax-l5p-lm2-can-bus-connector',
    canbus2023: '2020-2022-duramax-can-bus-connector',
    // switches
    dsp5: '2001-2016-duramax-dsp5-switch',
    csp5: '2006-2022-cummins-csp5-sotf-switch',
    ccvCummins: 'cummins-ccv-sotf-switch',
    fuelTempPs: '2008-2019-powerstroke-fuel-temp-universal-sotf-switch',
    ftpPs: '2015-2019-powerstroke-ftp-sotf-switch',
    cacPs: '2011-2022-powerstroke-cac-sotf-switch',
    switchBracket: 'universal-sotf-switch-mounting-bracket'
  };

  /*
    Route = one way to tune a given engine.
    deviceFamily: efilive | ezlynk | hptuners | sct | edge | amdp | hydra
    tune: { handle, pick: 'single-sotf' | 'variants', singleMatch, sotfMatch, variantFilter }
    license: 'efilive-vin' → add VIN license when device hasn't flashed this truck
             'hpt-credits' → offer credits qty when device hasn't flashed this truck
    devices: purchasable device handles for the "I need a device" path
    cables: [{handle, label, note}] offered as checkboxes
    switch: { handle, variantMatch } offered when SOTF picked
    includes: ['html', …] "What's Included" list on the package step
    sotfNote: shown above the SOTF switch on the options step
  */
  /* Power level lists pulled from each product page — keep in sync with product descriptions. */
  var PL = {
    dmaxEfiLive: [
      { label: 'Tow', hp: '+25HP' }, { label: 'Economy', hp: '+45HP' }, { label: 'Street', hp: '+75HP' },
      { label: 'Performance', hp: '+90HP' }, { label: 'Race', hp: '+120HP' }
    ],
    cmns5906: [
      { label: 'Tow', hp: '+25HP' }, { label: 'Street', hp: '+45HP' }, { label: 'Hot Street', hp: '+75HP' },
      { label: 'Performance', hp: '+90HP' }, { label: 'Race', hp: '+120HP' }
    ],
    cmns5903: [
      { label: 'Level 1', hp: '+25HP' }, { label: 'Level 2', hp: '+45HP' }, { label: 'Level 3', hp: '+75HP' },
      { label: 'Level 4', hp: '+90HP' }, { label: 'Level 5', hp: '+160HP' }
    ],
    cmns2224: [
      { label: 'Level 1', hp: '+50HP' }, { label: 'Level 2', hp: '+75HP' }, { label: 'Level 3', hp: '+90HP' },
      { label: 'Level 4', hp: '+120HP' }, { label: 'Level 5', hp: '+150HP' }
    ],
    l5p1723: [
      { label: 'Stock Delete', hp: 'stock power' }, { label: 'Level 2', hp: '+45HP' }, { label: 'Level 3', hp: '+75HP' },
      { label: 'Level 4', hp: '+90HP' }, { label: 'Level 5', hp: '+120HP' }
    ],
    ps60: [
      { label: 'Performance Locked', desc: 'Race tuning with early torque converter lockup — track or aggressive street.' },
      { label: 'Performance', desc: 'Flagship race tuning, quick shifts and maximum power. Studded trucks recommended.' },
      { label: 'Hot Street', desc: 'Race-style tuning for non-studded trucks, or street tune for studded trucks.' },
      { label: 'Street', desc: 'Hotter than tow — holds its own on the street, still tows.' },
      { label: 'Tow', desc: 'Everyday driveability and power for towing and daily driving.' },
      { label: 'Heavy 12k Tow', desc: 'Near-stock power with better driveability and spooling for heavy hauling.' },
      { label: 'Custom Request', desc: 'Drag trucks, standalone builds, or anything special — tell us below.', custom: true }
    ],
    ps60Hpt: [
      { label: 'Performance Locked', desc: 'Race tuning with early torque converter lockup — track or aggressive street.' },
      { label: 'Performance', desc: 'Flagship race tuning, quick shifts and maximum power. Studded trucks recommended.' },
      { label: 'Street', desc: 'Race-style tuning for non-studded trucks, or street tune for studded trucks.' },
      { label: 'Tow', desc: 'Everyday driveability and power for towing and daily driving.' },
      { label: 'Heavy 12k Tow', desc: 'A little above stock power with better driveability and spooling for heavy hauling.' },
      { label: 'Custom Request', desc: 'Drag trucks, standalone builds, or anything special — tell us below.', custom: true }
    ],
    ps73: [
      { label: 'Tow', hp: '40HP' }, { label: 'Street', hp: '90HP' },
      { label: 'Hot Street', hp: '110HP' }, { label: 'Performance', hp: '140HP' }
    ],
    ps64: [
      { label: 'Level 1', hp: '+50HP (approx)' }, { label: 'Level 2', hp: '+100HP (approx)' }, { label: 'Level 3', hp: '+150HP (approx)' },
      { label: 'Level 4', hp: '+200HP (approx)' }, { label: 'Level 5', hp: '+300HP (approx)' }
    ],
    ficm: ['+25HP', '+40HP', '+60HP']
  };
  var CMNS_67_BANDS = [
    { match: /2007-2009|2006-2009/, levels: [
      { label: 'Tow', hp: '+25HP' }, { label: 'Economy', hp: '+45HP' }, { label: 'Street', hp: '+75HP' }, { label: 'Race', hp: '+90HP' }
    ] },
    { match: /2010-2012/, levels: [
      { label: 'Tow', hp: '+25HP' }, { label: 'Economy', hp: '+45HP' }, { label: 'Daily', hp: '+75HP' }, { label: 'Street', hp: '+90HP' }, { label: 'Race', hp: '+120HP' }
    ] },
    { match: /2013-2018/, levels: [
      { label: 'Economy/Tow', hp: '+45HP' }, { label: 'Daily', hp: '+75HP' }, { label: 'Street', hp: '+90HP' }, { label: 'Race', hp: '+120HP' }
    ] },
    { match: /2019/, levels: [
      { label: 'Tow', hp: '+25HP' }, { label: 'Economy', hp: '+45HP' }, { label: 'Daily', hp: '+75HP' }, { label: 'Street', hp: '+90HP' }, { label: 'Race', hp: '+120HP' }
    ] }
  ];

  /* What's-included lists + SOTF notes for the EZ Lynk packages — keep in sync with the product descriptions. */
  var CMNS_CRUISE_NOTE = 'No switch needed — CCV-Cruise SOTF runs on your factory cruise control buttons. A CCV switch is optional; if you are not running one, the CCV sensor must be unplugged.';
  var CMNS_EZ_INCLUDES = [
    '<strong>CCV-Cruise SOTF on every year</strong> — five levels (50, 75, 90, 120 and 150HP). Hold Cruise Off for 2 seconds, then Set+ / Set- change levels on the fly. The level shows on your DEF gauge, and a CCV switch works too.',
    '<strong>Single-level files</strong> — Level 1 50HP through Level 5 150HP.',
    '<strong>68RFE and Aisin transmission tuning included</strong> — 68RFE standard, raised or high shift points with 180, 200 or 225PSI line pressure (plus Randy\'s and RevMax files); Aisin files for stock, 33", 35" and 37"+ tires.',
    '<strong>Hiss or Rumble idle</strong>, stock or modified VGT turbo, 5" Intake / S400 files and an optional 4000 RPM hardcut with low boost fueling.'
  ];
  var PS_CRUISE_NOTE = 'No switch needed for CMS6 Cruise SOTF — it runs on your factory cruise control buttons. Add the CAC switch only if you want to run the CAC SOTF files.';
  var PS_EZ_INCLUDES = [
    '<strong>CMS6 Cruise SOTF</strong> — six levels (25, 50, 75, 90, 120 and 150HP) switched on the fly with your factory cruise control buttons. No switch to install; the level menu shows on your tach.',
    '<strong>CAC SOTF and single-level files</strong> — Level 1 50HP through Level 5 150HP, with CAC SOTF files for the optional CAC switch.',
    '<strong>High idle in every engine file</strong> — 1,000 to 2,000 RPM, set from the cruise buttons in Park.',
    '<strong>Transmission tuning included</strong> — medium, raised or high shift points with a normal, 2nd gear start or 1-3 skip pattern.',
    '<strong>Hiss or Rumble idle</strong>, LBF hardcut options and KC / Titan turbo files on pickups.'
  ];

  var CATALOG = {
    duramax: {
      label: 'Duramax (GM/Chevrolet)',
      engines: [
        {
          id: 'dmax-01-10', label: '2001–2010 6.6L (LB7 / LLY / LBZ / LMM)',
          trans: { handle: '2001-2016-duramax-transmission-tuning', label: 'Allison Transmission Tuning (EFI Live)', note: 'A stock TCM read is required for transmission tuning.' },
          routes: [
            {
              deviceFamily: 'efilive', label: 'EFI Live (AutoCal / FlashScan)',
              tune: { handle: '2001-2010-duramax-efi-live-tune-files', pick: 'single-sotf', singleMatch: /single/i, sotfMatch: /sotf|dsp5/i, powerLevels: PL.dmaxEfiLive },
              license: 'efilive-vin',
              devices: [PRODUCTS.autocal],
              switches: [{ handle: PRODUCTS.dsp5, variantByEngine: true }]
            }
          ]
        },
        {
          id: 'dmax-lml', label: '2011–2016 6.6L LML',
          trans: { handle: '2001-2016-duramax-transmission-tuning', label: 'Allison Transmission Tuning (EFI Live)', note: 'A stock TCM read is required for transmission tuning.' },
          routes: [
            {
              deviceFamily: 'efilive', label: 'EFI Live (AutoCal / FlashScan)',
              tune: { handle: '2011-2016-lml-duramax-efi-live-tuning', pick: 'single-sotf', singleMatch: /^single/i, sotfMatch: /sotf/i, powerLevels: PL.dmaxEfiLive },
              license: 'efilive-vin',
              devices: [PRODUCTS.autocal],
              switches: [{ handle: PRODUCTS.dsp5, variantByEngine: true }]
            }
          ]
        },
        {
          id: 'dmax-l5p-17-23', label: '2017–2023 6.6L L5P',
          trans: null,
          transByRoute: {
            ezlynk: { handle: '2017-2019-duramax-ezlynk-tcm-tuning-add-on', label: 'Allison TCM Tuning Add-On (EZ Lynk)' },
            hptuners: { handle: '2017-2019-duramax-mpvi3-ccs-tcm-tuning', label: 'Allison TCM Tuning (HP Tuners)', extraCredits: 4 }
          },
          routes: [
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk Auto Agent',
              tune: { handle: '2017-2023-ezlynk-l5p-tuning', pick: 'variants' },
              devices: [PRODUCTS.ezlynk],
              switchAlways: true,
              sotfNote: 'No switch needed on the 2017–2023 L5P — switch-on-the-fly is controlled through the factory cruise control buttons.',
              gate1718: true,
              cables: [{ handle: PRODUCTS.canbus1722, label: 'CAN Bus Connector (required 2017–2023)', default: true }]
            },
            {
              deviceFamily: 'hptuners', label: 'HP Tuners (MPVI / RTD4)',
              tune: { handle: '2017-2023-duramax-mpvi2-mpvi3-tune-files', pick: 'single-sotf', singleMatch: /single/i, sotfMatch: /sotf/i, powerLevels: PL.l5p1723 },
              license: 'hpt-credits', creditsDefault: 4,
              devices: [PRODUCTS.mpvi4, PRODUCTS.rtd4],
              sotfNote: 'No switch needed on the 2017–2023 L5P — switch-on-the-fly is controlled through the factory cruise control buttons.',
              cables: [{ handle: PRODUCTS.canbus1722, label: 'CAN Bus Connector (required 2017–2023)', default: true }]
            }
          ]
        },
        {
          id: 'dmax-lm2', label: '2020–2022 LM2 3.0L',
          trans: null,
          routes: [
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk Auto Agent',
              tune: { handle: '2020-2022-lm2-duramax-ezlynk-tuning', pick: 'variants' },
              devices: [PRODUCTS.ezlynk]
            },
            {
              deviceFamily: 'hptuners', label: 'HP Tuners (MPVI / RTD4)',
              tune: { handle: '2020-2022-lm2-duramax-hp-tuners', pick: 'single-sotf', singleMatch: /single/i, sotfMatch: /5 tune|sotf/i, powerLevelsText: true },
              license: 'hpt-credits', creditsDefault: 6,
              devices: [PRODUCTS.mpvi4, PRODUCTS.rtd4],
              cables: [{ handle: PRODUCTS.canbus2023, label: 'CAN Bus Connector', default: true }]
            }
          ]
        },
        {
          id: 'dmax-l5p-24-26', label: '2024–2026 6.6L L5P',
          trans: null,
          routes: [
            {
              deviceFamily: 'hptuners', label: 'HP Tuners RTD4',
              tune: { handle: '2024-2026-l5p-duramax-hp-tuners-rtd4-files', pick: 'variants', powerLevelsText: true },
              license: 'hpt-credits', creditsDefault: 8,
              devices: [PRODUCTS.rtd4],
              e42Gate: true
            }
          ]
        }
      ]
    },

    cummins: {
      label: 'Cummins (Ram)',
      engines: [
        {
          id: 'cmns-03-05', label: '2003–2005 5.9L',
          trans: null,
          routes: [
            {
              deviceFamily: 'hptuners', label: 'HP Tuners (MPVI / RTD4)',
              tune: { handle: '2003-2005-5-9-cummins-hp-tuners-tune-files', pick: 'variants', variantFilter: /2003-2005/, powerLevels: PL.cmns5903 },
              license: 'hpt-credits', creditsDefault: 2,
              devices: [PRODUCTS.mpvi4, PRODUCTS.rtd4]
            }
          ]
        },
        {
          id: 'cmns-06-07', label: '2006–2007 5.9L',
          trans: null,
          routes: [
            {
              deviceFamily: 'efilive', label: 'EFI Live (AutoCal / FlashScan)',
              tune: { handle: '2006-2007-5-9-cummins-efi-live-files', pick: 'single-sotf', singleMatch: /single/i, sotfMatch: /sotf|switch/i, powerLevels: PL.cmns5906 },
              license: 'efilive-vin',
              devices: [PRODUCTS.autocal],
              switches: [{ handle: PRODUCTS.csp5 }]
            },
            {
              deviceFamily: 'hptuners', label: 'HP Tuners (MPVI / RTD4)',
              tune: { handle: '2003-2005-5-9-cummins-hp-tuners-tune-files', pick: 'variants', variantFilter: /2006-2007/, powerLevels: PL.cmns5903 },
              license: 'hpt-credits', creditsDefault: 2,
              devices: [PRODUCTS.mpvi4, PRODUCTS.rtd4]
            }
          ]
        },
        {
          id: 'cmns-67-early', label: '2007.5–2012 6.7L',
          trans: null,
          transByRoute: {
            efilive: { handle: '2007-5-cummins-efi-live-tcm-tuning', label: '68RFE TCM Tuning (EFI Live)' }
          },
          routes: [
            {
              deviceFamily: 'efilive', label: 'EFI Live (AutoCal / FlashScan)',
              tune: { handle: '2006-2021-cummins-efilive-custom-tune-files', pick: 'year-single-sotf', variantFilter: /2007-2009|2010-2012/, powerLevelsByMatch: CMNS_67_BANDS },
              license: 'efilive-vin',
              requiresEcmTag: true, ecmTagExclude: /2007-2009/,
              devices: [PRODUCTS.autocal],
              switches: [{ handle: PRODUCTS.csp5 }]
            }
          ]
        },
        {
          id: 'cmns-67-late', label: '2013–2021 6.7L',
          trans: null,
          transByRoute: {
            efilive: { handle: '2007-5-cummins-efi-live-tcm-tuning', label: '68RFE TCM Tuning (EFI Live)' }
          },
          routes: [
            {
              deviceFamily: 'efilive', label: 'EFI Live (AutoCal / FlashScan)',
              tune: { handle: '2006-2021-cummins-efilive-custom-tune-files', pick: 'year-single-sotf', variantFilter: /2013-2018|2019/, powerLevelsByMatch: CMNS_67_BANDS },
              license: 'efilive-vin',
              requiresEcmTag: true,
              devices: [PRODUCTS.autocal],
              cables: [{ handle: PRODUCTS.cumminsPatchCable, label: 'OBDII Patch Cable (required on 2018+ trucks)', default: false }],
              switches: [{ handle: PRODUCTS.csp5 }]
            },
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk Auto Agent',
              tune: { handle: '2013-2021-cummins-ezlynk-support-packages', pick: 'variants' },
              includes: CMNS_EZ_INCLUDES,
              devices: [PRODUCTS.ezlynk],
              // Brent 2026-10-12: EZ Lynk packages only offer the EZ Lynk OBDII patch cable on 2018-2024 trucks (no bypass cable)
              cables: [{ handle: PRODUCTS.cumminsPatchCable, label: 'EZ Lynk OBDII Patch Cable (required on 2018–2024 trucks)', default: false }],
              sotfNote: CMNS_CRUISE_NOTE,
              switches: [{ handle: PRODUCTS.ccvCummins }], switchAlways: true
            }
          ]
        },
        {
          id: 'cmns-22-24', label: '2022–2024 6.7L',
          trans: null,
          routes: [
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk Auto Agent',
              tune: { handle: '22-24-6-7-cummins-ezlynk-support-packages', pick: 'variants' },
              infoNote: 'No ECM swap needed — your tune loads straight onto the factory ECM through EZ Lynk. Files are sorted by the E/C number on your ECM tag.',
              includes: CMNS_EZ_INCLUDES,
              devices: [PRODUCTS.ezlynk],
              requiresEcmEc: true,
              // Brent 2026-10-12: EZ Lynk packages only offer the EZ Lynk OBDII patch cable on 2018-2024 trucks (no bypass cable)
              cables: [
                { handle: PRODUCTS.cumminsPatchCable, label: 'EZ Lynk OBDII Patch Cable (required on 2018–2024 trucks)', default: false },
                { handle: PRODUCTS.cumminsDiagCable, label: 'Diagnostics Cable (ECM-swapped trucks only)', default: false }
              ],
              sotfNote: CMNS_CRUISE_NOTE,
              switches: [{ handle: PRODUCTS.ccvCummins }], switchAlways: true
            },
            {
              deviceFamily: 'hptuners', label: 'HP Tuners (MPVI / RTD4)',
              tune: { handle: '2022-2024-6-7-cummins-hp-tuners-tune-files', pick: 'single-sotf', singleMatch: /single/i, sotfMatch: /sotf/i, powerLevels: PL.cmns2224 },
              license: 'hpt-credits', creditsDefault: 6,
              infoNote: 'No ECM unlock or swap needed — HP Tuners now has full flash support on the factory 2022–2024 ECM. The SGW bypass cable is required on all 2018+ Ram trucks.',
              devices: [PRODUCTS.mpvi4, PRODUCTS.rtd4],
              cables: [{ handle: PRODUCTS.cumminsBypassCable, label: 'Bypass Cable (2018–2024, required to flash)', default: false }]
            }
          ]
        }
      ]
    },

    powerstroke: {
      label: 'Powerstroke (Ford)',
      engines: [
        {
          id: 'ps-73', label: '1995–2003 7.3L',
          trans: null,
          routes: [
            {
              deviceFamily: 'sct', label: 'SCT (X4 / Livewire)',
              tune: { handle: '1997-2003-7-3-powerstroke-sct-custom-tune-files', pick: 'variants', powerLevels: PL.ps73 },
              devices: [PRODUCTS.sctx4, PRODUCTS.livewire],
              orderFields: [{ id: 'ecuStrategy', label: 'Stock ECU Strategy code', hint: 'Shown on your SCT device under vehicle info when connected to the truck.' }]
            },
            {
              deviceFamily: 'hydra', label: 'Hydra Chip (SDK Custom Tuning)',
              tune: { handle: '7-3-hydra-chip', pick: 'variants' },
              devices: [],
              orderFields: [
                { id: 'hydraSerial', label: 'Hydra serial number', ownedOnly: true, hint: 'On your Hydra chip.' },
                { id: 'hydraHex', label: 'Hydra hex code', ownedOnly: true, hint: 'From your Hydra chip / PHP software.' },
                { id: 'pcmCode', label: 'PCM code', hint: 'The 4-character code on your PCM sticker (e.g. TDE1, XLE7).' }
              ]
            }
          ]
        },
        {
          id: 'ps-60', label: '2003–2010 6.0L',
          trans: null,
          routes: [
            {
              deviceFamily: 'sct', label: 'SCT (X4 / Livewire)',
              tune: { handle: '2003-2007-6-0-powerstroke-custom-tuning', pick: 'variants', powerLevels: PL.ps60 },
              devices: [PRODUCTS.sctx4, PRODUCTS.livewire],
              orderFields: [{ id: 'ecuStrategy', label: 'Stock ECU Strategy code', hint: 'Shown on your SCT device under vehicle info when connected to the truck.' }]
            },
            {
              deviceFamily: 'hptuners', label: 'HP Tuners (MPVI / RTD4)',
              tune: { handle: '2003-2010-6-0-powerstroke-custom-tuning-hp-tuners', pick: 'variants', powerLevels: PL.ps60Hpt },
              license: 'hpt-credits', creditsDefault: 2,
              devices: [PRODUCTS.mpvi4, PRODUCTS.rtd4],
              extras: [{ handle: '2003-2007-6-0-powerstroke-custom-ficm-tuning', label: 'FICM Tuning Add-On (HP Tuners)', powerLevels: PL.ficm, extraCredits: 2 }]
            },
            {
              deviceFamily: 'edge', label: 'Edge CTS3 Insight',
              tune: { handle: '2003-2010-6-0-powerstroke-edge-cts3-insite-custom-tune-files', pick: 'variants', powerLevels: PL.ps60 },
              infoNote: 'Your CTS3 must be updated via Edge\'s Update Agent to use these files, and stock reads must be emailed to sales@peaksideperformance.ca with your order number. These files keep emissions equipment intact.',
              devices: ['universal-edge-insight-cts3-monitor-84130-3']
            }
          ]
        },
        {
          id: 'ps-64', label: '2008–2010 6.4L',
          trans: null,
          routes: [
            {
              deviceFamily: 'sct', label: 'SCT (X4 / Livewire)',
              tune: { handle: '2008-2010-powerstroke-sct-custom-tune-files', pick: 'variants', powerLevels: PL.ps64 },
              devices: [PRODUCTS.sctx4, PRODUCTS.livewire],
              switches: [{ handle: PRODUCTS.fuelTempPs }],
              orderFields: [{ id: 'tcuStrategy', label: 'Stock TCU Strategy code', hint: 'Shown on your SCT device under vehicle info when connected to the truck.' }]
            },
            {
              deviceFamily: 'hptuners', label: 'HP Tuners (MPVI / RTD4)',
              tune: { handle: '2008-2010-6-4-powerstroke-hp-tuners-mpvi-rtd4-custom-tune-files', pick: 'variants', powerLevels: PL.ps64 },
              license: 'hpt-credits', creditsDefault: 4,
              infoNote: 'TCM tuning is included but needs 2 additional credits (already counted below), and the stock TCM read must be sent separately.',
              devices: [PRODUCTS.mpvi4, PRODUCTS.rtd4]
            },
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk Auto Agent',
              tune: { handle: '2008-2010-6-4-powerstroke-ez-lynk-tuning', pick: 'variants' },
              devices: [PRODUCTS.ezlynk],
              switches: [{ handle: PRODUCTS.fuelTempPs }], switchAlways: true
            }
          ]
        },
        {
          id: 'ps-67-11-19', label: '2011–2019 6.7L',
          trans: null,
          routes: [
            {
              deviceFamily: 'sct', label: 'SCT (AMDP Custom Tunes)',
              tune: { handle: '2008-2019-powerstroke-amdp-sct-custom-tune-files', pick: 'single-sotf', singleMatch: /single/i, sotfMatch: /sotf/i, powerLevelsText: true },
              infoNote: 'These files are custom-written by AM Diesel Performance (AMDP) — from optimized stock up to 120HP.',
              devices: [PRODUCTS.sctx4, PRODUCTS.livewire],
              switches: [{ handle: PRODUCTS.cacPs }, { handle: PRODUCTS.fuelTempPs }]
            },
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk (AMDP Support Package)',
              tune: { handle: '2008-2019-powerstroke-amdp-support-package', pick: 'variants' },
              infoNote: 'These tunes are custom-written by AM Diesel Performance (AMDP) and delivered through EZ Lynk — from optimized stock up to 300HP.',
              devices: [PRODUCTS.ezlynk],
              switches: [{ handle: PRODUCTS.cacPs }, { handle: PRODUCTS.fuelTempPs }], switchAlways: true
            }
          ]
        },
        {
          id: 'ps-67-20-22', label: '2020–2022 6.7L',
          trans: null,
          routes: [
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk Auto Agent',
              tune: { handle: '2020-2022-6-7-powerstroke-ezlynk-tuning', pick: 'variants' },
              includes: PS_EZ_INCLUDES,
              devices: [PRODUCTS.ezlynk],
              sotfNote: PS_CRUISE_NOTE,
              switches: [{ handle: PRODUCTS.cacPs }], switchAlways: true
            }
          ]
        },
        {
          id: 'ps-67-23-26', label: '2023–2027 6.7L',
          trans: null,
          routes: [
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk Auto Agent',
              tune: { handle: '2023-2026-6-7-powerstroke-ezlynk-tuning', pick: 'variants' },
              includes: PS_EZ_INCLUDES,
              devices: [PRODUCTS.ezlynk],
              sotfNote: PS_CRUISE_NOTE,
              switches: [{ handle: PRODUCTS.cacPs }], switchAlways: true
            }
          ]
        },
        {
          id: 'ps-30', label: '2018–2020 3.0L',
          trans: null,
          routes: [
            {
              deviceFamily: 'ezlynk', label: 'EZ Lynk Auto Agent',
              tune: { handle: '2018-2020-3-0-powerstroke-ez-lynk-support-packages', pick: 'variants' },
              devices: [PRODUCTS.ezlynk]
            }
          ]
        }
      ]
    },

    ecodiesel: {
      label: 'EcoDiesel (Ram)',
      engines: [
        {
          id: 'eco-14-19', label: '2014–2019 3.0L',
          trans: null,
          routes: [
            {
              deviceFamily: 'hptuners', label: 'HP Tuners (MPVI / RTD4)',
              tune: { handle: '2014-2019-ram-eco-diesel-hp-tuners-mpvi3-rtd4-custom-tuning', pick: 'variants', powerLevelsText: true },
              license: 'hpt-credits', creditsDefault: 2,
              devices: [PRODUCTS.mpvi4, PRODUCTS.rtd4]
            }
          ]
        }
      ]
    }
  };

  var DEVICE_FAMILY_LABELS = {
    efilive: 'EFI Live AutoCal / FlashScan',
    ezlynk: 'EZ Lynk Auto Agent 2.0 / 3.0',
    hptuners: 'HP Tuners MPVI2 / MPVI3 / MPVI4 / RTD4',
    sct: 'SCT X4 / Livewire',
    edge: 'Edge CTS3',
    amdp: 'AMDP Programmer',
    hydra: 'Hydra Chip'
  };

  // DSP5 switch variant matcher per Duramax engine id
  var DSP5_VARIANT = {
    'dmax-01-10': /LB7|LLY|LBZ|LMM/i,
    'dmax-lml': /LML/i
  };

  /* =========================== PRODUCT CACHE =========================== */

  var productCache = {};
  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + handle + '.js')
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .catch(function () { return null; });
    return productCache[handle];
  }
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ============================ WIZARD STATE =========================== */

  var state = {
    platform: null, engine: null,
    hasDevice: null, deviceFamily: null, flashedBefore: null,
    route: null, buyDevice: null,
    tuneVariant: null, sotf: null,
    switchSel: {}, extraSel: {}, powerSel: [], customRequest: '',
    creditsTouched: false,
    l5p1718: null, e42Unlock: null, linkCodes: null,
    wantTrans: false, wantSupport: false, supportVariant: null,
    cables: {}, creditsQty: 0,
    form: {},
    vin: { status: '', text: '', warn: '', decoded: null, auto: '', seq: 0 }
  };

  var root, stepsEl, bodyEl, backBtn, nextBtn, summaryEl;
  var currentStep = 0;

  function stepList() {
    var steps = [
      { id: 'truck', title: 'Your Truck' },
      { id: 'device', title: 'Tuning Device' }
    ];
    if (state.hasDevice === true) steps.push({ id: 'flashed', title: 'Device History' });
    steps.push({ id: 'route', title: 'Tuning Package' });
    steps.push({ id: 'options', title: 'Options & Add-Ons' });
    steps.push({ id: 'order', title: 'Order Details' });
    steps.push({ id: 'review', title: 'Review & Add to Cart' });
    return steps;
  }

  /* ============================= RENDERING ============================= */

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  function renderProgress() {
    var steps = stepList();
    stepsEl.innerHTML = '';
    steps.forEach(function (s, i) {
      var li = el('li', 'tc-step' + (i === currentStep ? ' tc-step--active' : i < currentStep ? ' tc-step--done' : ''));
      li.innerHTML = '<span class="tc-step__num">' + (i + 1) + '</span><span class="tc-step__label">' + s.title + '</span>';
      stepsEl.appendChild(li);
    });
  }

  function cardGrid(options, selectedId, onPick) {
    var grid = el('div', 'tc-grid');
    options.forEach(function (o) {
      var c = el('button', 'tc-card' + (selectedId === o.id ? ' tc-card--selected' : ''));
      c.type = 'button';
      c.innerHTML = '<span class="tc-card__title">' + o.label + '</span>' +
        (o.sub ? '<span class="tc-card__sub">' + o.sub + '</span>' : '') +
        (o.price ? '<span class="tc-card__price">' + o.price + '</span>' : '');
      c.addEventListener('click', function () { onPick(o); });
      grid.appendChild(c);
    });
    return grid;
  }

  function render() {
    renderProgress();
    var steps = stepList();
    var step = steps[currentStep];
    bodyEl.innerHTML = '';
    backBtn.style.visibility = currentStep === 0 ? 'hidden' : 'visible';
    nextBtn.textContent = step.id === 'review' ? 'Add to Cart' : 'Continue';
    ({
      truck: renderTruck, device: renderDevice, flashed: renderFlashed,
      route: renderRoute, options: renderOptions, order: renderOrder, review: renderReview
    })[step.id]();
  }

  function renderTruck() {
    bodyEl.appendChild(el('h3', 'tc-h', 'What are we tuning?'));
    var platforms = Object.keys(CATALOG).map(function (k) { return { id: k, label: CATALOG[k].label }; });
    bodyEl.appendChild(cardGrid(platforms, state.platform, function (o) {
      if (state.platform !== o.id) { state.platform = o.id; state.engine = null; resetDownstream('platform'); }
      render();
    }));
    if (state.platform) {
      bodyEl.appendChild(el('h3', 'tc-h', 'Which engine / year range?'));
      var engines = CATALOG[state.platform].engines.map(function (e) { return { id: e.id, label: e.label }; });
      bodyEl.appendChild(cardGrid(engines, state.engine && state.engine.id, function (o) {
        var eng = CATALOG[state.platform].engines.find(function (e) { return e.id === o.id; });
        if (!state.engine || state.engine.id !== eng.id) { state.engine = eng; resetDownstream('engine'); }
        render();
      }));
    }
  }

  function renderDevice() {
    bodyEl.appendChild(el('h3', 'tc-h', 'Do you already own a tuning device?'));
    bodyEl.appendChild(cardGrid([
      { id: 'yes', label: 'Yes, I have a device', sub: 'AutoCal, FlashScan, EZ Lynk, MPVI, RTD4, SCT…' },
      { id: 'no', label: 'No, I\'m starting fresh', sub: 'We\'ll add the right device to your order' }
    ], state.hasDevice === null ? null : (state.hasDevice ? 'yes' : 'no'), function (o) {
      var v = o.id === 'yes';
      if (state.hasDevice !== v) { state.hasDevice = v; resetDownstream('device'); }
      render();
    }));

    if (state.hasDevice === true && state.engine) {
      bodyEl.appendChild(el('h3', 'tc-h', 'Which device do you have?'));
      var fams = {};
      state.engine.routes.forEach(function (r) { fams[r.deviceFamily] = true; });
      var opts = Object.keys(fams).map(function (f) { return { id: f, label: DEVICE_FAMILY_LABELS[f] || f }; });
      bodyEl.appendChild(cardGrid(opts, state.deviceFamily, function (o) {
        if (state.deviceFamily !== o.id) { state.deviceFamily = o.id; resetDownstream('family'); }
        render();
      }));
      var note = el('p', 'tc-note', 'Don\'t see your device? <a href="/pages/contact">Contact us</a> and we\'ll sort you out.');
      bodyEl.appendChild(note);
    }
  }

  function renderFlashed() {
    bodyEl.appendChild(el('h3', 'tc-h', 'Has this device ever flashed <em>this</em> truck?'));
    bodyEl.appendChild(el('p', 'tc-note', 'This tells us whether you need licensing or tuning credits for a new VIN.'));
    bodyEl.appendChild(cardGrid([
      { id: 'yes', label: 'Yes — it\'s tuned this truck before', sub: 'No new licensing needed' },
      { id: 'no', label: 'No — first time on this truck', sub: 'We\'ll include the required license / credits' }
    ], state.flashedBefore === null ? null : (state.flashedBefore ? 'yes' : 'no'), function (o) {
      state.flashedBefore = o.id === 'yes';
      render();
    }));
  }

  function renderRoute() {
    var routes = availableRoutes();
    bodyEl.appendChild(el('h3', 'tc-h', 'Pick your tuning package'));
    var grid = el('div', 'tc-grid');
    bodyEl.appendChild(grid);
    routes.forEach(function (r) {
      var c = el('button', 'tc-card' + (state.route === r ? ' tc-card--selected' : ''));
      c.type = 'button';
      c.innerHTML = '<span class="tc-card__title">' + r.label + '</span><span class="tc-card__sub tc-price-slot">Loading price…</span>';
      fetchProduct(r.tune.handle).then(function (p) {
        var slot = c.querySelector('.tc-price-slot');
        if (p) {
          var prices = p.variants.map(function (v) { return v.price; });
          var mn = Math.min.apply(null, prices), mx = Math.max.apply(null, prices);
          slot.textContent = (mn === mx ? money(mn) : money(mn) + ' – ' + money(mx));
        } else slot.textContent = 'See options';
      });
      c.addEventListener('click', function () {
        if (state.route !== r) { state.route = r; resetDownstream('route'); }
        render();
      });
      grid.appendChild(c);
    });

    // 2017–2018 L5P: EZ Lynk can't tune a locked ECM — offer HP Tuners instead
    if (state.route && state.route.gate1718 && (state.hasDevice === false || state.flashedBefore === false)) {
      bodyEl.appendChild(el('h3', 'tc-h', 'Is your truck a 2017 or 2018?'));
      bodyEl.appendChild(el('p', 'tc-note', 'EZ Lynk can only tune a 2017–2018 L5P if the ECM has already been unlocked or tuned before. For those trucks we tune with HP Tuners instead.'));
      bodyEl.appendChild(cardGrid([
        { id: 'yes', label: 'Yes — 2017 or 2018', sub: 'We\'ll switch you to the HP Tuners package' },
        { id: 'no', label: 'No — 2019 to 2023', sub: 'EZ Lynk works great, carry on' }
      ], state.l5p1718 === null ? null : (state.l5p1718 ? 'yes' : 'no'), function (o) {
        state.l5p1718 = o.id === 'yes';
        if (state.l5p1718) {
          var hpt = state.engine.routes.find(function (rt) { return rt.deviceFamily === 'hptuners'; });
          if (hpt) { resetDownstream('route'); state.route = hpt; }
        }
        render();
      }));
    }
    if (state.route && state.route.deviceFamily === 'hptuners' && state.l5p1718 === true) {
      bodyEl.appendChild(el('p', 'tc-note', 'Because your 2017–2018 L5P hasn\'t been tuned before, EZ Lynk isn\'t an option — we\'ve moved you to the HP Tuners package.'));
    }

    if (state.route) {
      // what the package includes (EZ Lynk packages)
      if (state.route.includes) {
        bodyEl.appendChild(el('h3', 'tc-h', 'What\'s Included'));
        var inc = el('ul', 'tc-note tc-includes');
        inc.style.paddingLeft = '1.8rem';
        state.route.includes.forEach(function (t) { inc.appendChild(el('li', null, t)); });
        bodyEl.appendChild(inc);
      }

      // tune variant selection
      var holder = el('div', 'tc-variants');
      bodyEl.appendChild(el('h3', 'tc-h', 'Tune option'));
      bodyEl.appendChild(holder);
      fetchProduct(state.route.tune.handle).then(function (p) {
        if (!p) { holder.innerHTML = '<p class="tc-note">Product unavailable — please contact us.</p>'; return; }
        holder.innerHTML = '';
        var variants = p.variants.filter(function (v) { return v.available !== false; });
        if (state.route.tune.variantFilter) variants = variants.filter(function (v) { return state.route.tune.variantFilter.test(v.title); });
        variants.forEach(function (v) {
          var r = el('label', 'tc-radio');
          r.innerHTML = '<input type="radio" name="tunevariant"' + (state.tuneVariant && state.tuneVariant.id === v.id ? ' checked' : '') + '>' +
            '<span>' + (v.title === 'Default Title' ? p.title : v.title) + '</span><strong>' + money(v.price) + '</strong>';
          r.querySelector('input').addEventListener('change', function () {
            state.tuneVariant = v;
            state.sotf = /sotf|switch on the fly|dsp5|csp5|5 tune levels|all levels/i.test(v.title);
            state.powerSel = []; state.customRequest = '';
            render();
          });
          holder.appendChild(r);
        });
      });

      if (state.route.infoNote) bodyEl.appendChild(el('p', 'tc-note', state.route.infoNote));

      // power level selection for single/multi-file tunes (EZ Lynk excluded)
      var levels = powerLevelsFor();
      if (levels) {
        var n = levelCount(state.tuneVariant.title);
        if (n > 0) {
          bodyEl.appendChild(el('h3', 'tc-h', n === 1 ? 'Pick your power level' : 'Pick your ' + n + ' power levels'));
          bodyEl.appendChild(el('p', 'tc-note', 'These are the levels offered for your truck — higher levels can be written with the proper supporting modifications (tell us in the notes).'));
          var pHolder = el('div', 'tc-variants');
          bodyEl.appendChild(pHolder);
          var customBox = null;
          function levelKey(l) { return l.label + (l.hp ? ' (' + l.hp + ')' : ''); }
          function syncCustomBox() {
            var customDef = levels.map(function (l) { return typeof l === 'string' ? { label: l } : l; })
              .find(function (l) { return l.custom && state.powerSel.indexOf(levelKey(l)) >= 0; });
            if (customDef && !customBox) {
              customBox = el('div', 'tc-field tc-field--custom');
              customBox.innerHTML = '<label for="tc-customRequest">Describe your custom request <span class="tc-req">*</span></label>';
              var ta = document.createElement('textarea');
              ta.id = 'tc-customRequest';
              ta.placeholder = 'e.g. drag truck, standalone build, specific power target…';
              ta.value = state.customRequest || '';
              ta.addEventListener('input', function () { state.customRequest = ta.value.trim(); customBox.classList.remove('tc-field--error'); });
              customBox.appendChild(ta);
              pHolder.appendChild(customBox);
            } else if (!customDef && customBox) {
              pHolder.removeChild(customBox);
              customBox = null;
            }
          }
          levels.forEach(function (raw) {
            var lv = typeof raw === 'string' ? { label: raw } : raw;
            var key = levelKey(lv);
            var cb = el('label', 'tc-check');
            cb.innerHTML = '<input type="checkbox"' + (state.powerSel.indexOf(key) >= 0 ? ' checked' : '') + '>' +
              '<span>' + lv.label + (lv.desc ? '<small class="tc-lvl-desc">' + lv.desc + '</small>' : '') + '</span>' +
              (lv.hp ? '<strong>' + lv.hp + '</strong>' : '');
            cb.querySelector('input').addEventListener('change', function (e) {
              if (e.target.checked) {
                if (state.powerSel.length >= n) { e.target.checked = false; return; }
                state.powerSel.push(key);
              } else {
                state.powerSel = state.powerSel.filter(function (x) { return x !== key; });
              }
              syncCustomBox();
            });
            pHolder.appendChild(cb);
          });
          syncCustomBox();
        }
      }

      // 2024–2026 L5P: ECM must be unlocked before tuning
      if (state.route.e42Gate && state.tuneVariant) {
        bodyEl.appendChild(el('h3', 'tc-h', 'Have you unlocked your ECM yet?'));
        bodyEl.appendChild(el('p', 'tc-note', '2024–2026 L5P (Global B) ECMs must be unlocked before they can be tuned.'));
        bodyEl.appendChild(cardGrid([
          { id: 'yes', label: 'Yes, I have', sub: 'ECM is already unlocked' },
          { id: 'inprogress', label: 'It\'s currently being unlocked', sub: 'Unlock service is underway' },
          { id: 'no', label: 'No, I still need to', sub: 'We\'ll point you to the unlock service' }
        ], state.e42Unlock, function (o) { state.e42Unlock = o.id; render(); }));
        if (state.e42Unlock === 'no') {
          bodyEl.appendChild(el('p', 'tc-note',
            'You\'ll need the HP Tuners <a href="https://www.hptuners.com/product/gm-e42-ecm-service-l5p-global-b/" target="_blank" rel="noopener">GM E42 ECM Service (L5P Global B)</a> first. Once your unlock is on the way, choose "It\'s currently being unlocked" to continue.'));
        }
      }

      // device purchase if needed
      if (state.hasDevice === false && state.route.devices && state.route.devices.length) {
        bodyEl.appendChild(el('h3', 'tc-h', 'Your new device'));
        var dHolder = el('div', 'tc-variants');
        bodyEl.appendChild(dHolder);
        state.route.devices.forEach(function (h) {
          fetchProduct(h).then(function (p) {
            if (!p) return;
            var v = p.variants[0];
            var r = el('label', 'tc-radio');
            r.innerHTML = '<input type="radio" name="buydevice"' + (state.buyDevice && state.buyDevice.handle === h ? ' checked' : '') + '>' +
              '<span>' + p.title + '</span><strong>' + money(v.price) + '</strong>';
            r.querySelector('input').addEventListener('change', function () {
              state.buyDevice = { handle: h, variant: v, title: p.title };
            });
            dHolder.appendChild(r);
          });
        });
      }
    }
  }

  function renderOptions() {
    var r = state.route;
    var any = false;

    // SOTF switch hardware (per-vehicle) + cruise-control note
    var showSwitchArea = r && (state.sotf || r.switchAlways);
    if (showSwitchArea && (r.sotfNote || (r.switches && r.switches.length))) {
      any = true;
      bodyEl.appendChild(el('h3', 'tc-h', 'Switch-on-the-Fly hardware'));
      if (r.sotfNote) bodyEl.appendChild(el('p', 'tc-note', r.sotfNote));
      if (r.switches && r.switches.length) {
        var sw = el('div', 'tc-variants'); bodyEl.appendChild(sw);
        r.switches.forEach(function (def) {
          fetchProduct(def.handle).then(function (p) {
            if (!p) return;
            var variants = p.variants;
            if (def.variantByEngine && DSP5_VARIANT[state.engine.id]) {
              variants = variants.filter(function (v) { return DSP5_VARIANT[state.engine.id].test(v.title); });
            }
            var checked = !!state.switchSel[def.handle];
            var cb = el('label', 'tc-check');
            cb.innerHTML = '<input type="checkbox"' + (checked ? ' checked' : '') + '><span>Add ' + p.title + '</span><strong class="tc-sw-price"></strong>';
            sw.appendChild(cb);
            var sel = null;
            if (variants.length > 1) {
              sel = document.createElement('select'); sel.className = 'tc-select';
              variants.forEach(function (v, i) {
                var o = document.createElement('option'); o.value = i; o.textContent = v.title + ' — ' + money(v.price);
                sel.appendChild(o);
              });
              sw.appendChild(sel);
            }
            function sync() {
              var v = variants[sel ? +sel.value : 0];
              var on = cb.querySelector('input').checked;
              if (on) state.switchSel[def.handle] = { variant: v, title: p.title };
              else delete state.switchSel[def.handle];
              cb.querySelector('.tc-sw-price').textContent = money(v.price);
            }
            cb.querySelector('input').addEventListener('change', sync);
            if (sel) sel.addEventListener('change', sync);
            cb.querySelector('.tc-sw-price').textContent = money(variants[0].price);
          });
        });
      }
    }

    // transmission tuning
    var trans = engineTrans();
    if (trans) {
      any = true;
      bodyEl.appendChild(el('h3', 'tc-h', 'Transmission tuning'));
      var tHolder = el('div', 'tc-variants'); bodyEl.appendChild(tHolder);
      fetchProduct(trans.handle).then(function (p) {
        if (!p) return;
        var v = p.variants[0];
        var cb = el('label', 'tc-check');
        cb.innerHTML = '<input type="checkbox"' + (state.wantTrans ? ' checked' : '') + '><span>Add ' + (trans.label || p.title) +
          (trans.note ? '<small class="tc-lvl-desc">' + trans.note + '</small>' : '') + '</span><strong>' + money(v.price) + '</strong>';
        cb.querySelector('input').addEventListener('change', function (e) { state.wantTrans = e.target.checked; if (trans.extraCredits) render(); });
        tHolder.appendChild(cb);
      });
    }

    // route-specific extra add-ons (e.g. 6.0 FICM tuning)
    if (r && r.extras && r.extras.length) {
      any = true;
      bodyEl.appendChild(el('h3', 'tc-h', 'Add-on tuning'));
      var xHolder = el('div', 'tc-variants'); bodyEl.appendChild(xHolder);
      r.extras.forEach(function (def) {
        fetchProduct(def.handle).then(function (p) {
          if (!p) return;
          var variants = p.variants;
          var checked = !!state.extraSel[def.handle];
          var cb = el('label', 'tc-check');
          cb.innerHTML = '<input type="checkbox"' + (checked ? ' checked' : '') + '><span>Add ' + (def.label || p.title) + '</span><strong class="tc-x-price"></strong>';
          xHolder.appendChild(cb);
          var sel = null;
          if (variants.length > 1) {
            sel = document.createElement('select'); sel.className = 'tc-select';
            variants.forEach(function (v, i) {
              var o = document.createElement('option'); o.value = i; o.textContent = v.title + ' — ' + money(v.price);
              sel.appendChild(o);
            });
            xHolder.appendChild(sel);
          }
          function sync(rerender) {
            var v = variants[sel ? +sel.value : 0];
            var on = cb.querySelector('input').checked;
            if (on) state.extraSel[def.handle] = { variant: v, title: def.label || p.title, def: def };
            else delete state.extraSel[def.handle];
            cb.querySelector('.tc-x-price').textContent = money(v.price);
            // add-ons like FICM change the credits needed — refresh the step
            if (rerender && def.extraCredits) render();
          }
          cb.querySelector('input').addEventListener('change', function () { sync(true); });
          if (sel) sel.addEventListener('change', function () { sync(false); });
          cb.querySelector('.tc-x-price').textContent = money(variants[0].price);
        });
      });
    }

    // support package (AMDP)
    if (r && r.support) {
      any = true;
      bodyEl.appendChild(el('h3', 'tc-h', r.support.label));
      var sHolder = el('div', 'tc-variants'); bodyEl.appendChild(sHolder);
      fetchProduct(r.support.handle).then(function (p) {
        if (!p) return;
        p.variants.forEach(function (v) {
          var rr = el('label', 'tc-radio');
          rr.innerHTML = '<input type="radio" name="support"' + (state.supportVariant && state.supportVariant.id === v.id ? ' checked' : '') + '>' +
            '<span>' + v.title + '</span><strong>' + money(v.price) + '</strong>';
          rr.querySelector('input').addEventListener('change', function () { state.wantSupport = true; state.supportVariant = v; });
          sHolder.appendChild(rr);
        });
        var none = el('label', 'tc-radio');
        none.innerHTML = '<input type="radio" name="support"' + (!state.wantSupport ? ' checked' : '') + '><span>No support package</span>';
        none.querySelector('input').addEventListener('change', function () { state.wantSupport = false; state.supportVariant = null; });
        sHolder.appendChild(none);
      });
    }

    // cables
    if (r && r.cables && r.cables.length) {
      any = true;
      bodyEl.appendChild(el('h3', 'tc-h', 'Cables & connectors'));
      var cHolder = el('div', 'tc-variants'); bodyEl.appendChild(cHolder);
      r.cables.forEach(function (c) {
        fetchProduct(c.handle).then(function (p) {
          if (!p) return;
          if (!(c.handle in state.cables) && c.default && state.hasDevice === false) state.cables[c.handle] = true;
          var v = p.variants[0];
          var cb = el('label', 'tc-check');
          cb.innerHTML = '<input type="checkbox"' + (state.cables[c.handle] ? ' checked' : '') + '><span>' + (c.label || p.title) + '</span><strong>' + money(v.price) + '</strong>';
          cb.querySelector('input').addEventListener('change', function (e) { state.cables[c.handle] = e.target.checked; });
          cHolder.appendChild(cb);
        });
      });
    }

    // licensing / credits
    var lic = licenseNeed();
    var showLinkCodes = r && r.deviceFamily === 'efilive' && state.hasDevice === true;
    var showNewAutocalNote = r && r.license === 'efilive-vin' && state.hasDevice === false;
    if (lic || showLinkCodes || showNewAutocalNote) {
      any = true;
      bodyEl.appendChild(el('h3', 'tc-h', 'Licensing'));
      var lHolder = el('div', 'tc-variants'); bodyEl.appendChild(lHolder);
      if (showNewAutocalNote) {
        lHolder.appendChild(el('p', 'tc-note', 'Your new AutoCal comes with one VIN license included — no extra license needed for this truck. We\'ll link it to Peakside before it ships.'));
      }
      if (lic && lic.type === 'efilive-vin') {
        fetchProduct(PRODUCTS.efiliveLicense).then(function (p) {
          if (!p) return;
          lHolder.appendChild(el('p', 'tc-note', 'Your ' + DEVICE_FAMILY_LABELS.efilive + ' needs a VIN license for a truck it hasn\'t flashed before. Added automatically:'));
          lHolder.appendChild(el('div', 'tc-line', p.title + ' — <strong>' + money(p.variants[0].price) + '</strong>'));
        });
      }
      if (showLinkCodes) {
        lHolder.appendChild(el('p', 'tc-note', 'Do we need to provide link codes for your AutoCal?'));
        [['need', 'Yes — please send me link codes for my AutoCal'], ['linked', 'No — I\'m already linked with Peakside or I own a FlashScan']].forEach(function (opt) {
          var rr = el('label', 'tc-radio');
          rr.innerHTML = '<input type="radio" name="linkcodes"' + (state.linkCodes === opt[0] ? ' checked' : '') + '><span>' + opt[1] + '</span>';
          rr.querySelector('input').addEventListener('change', function () { state.linkCodes = opt[0]; });
          lHolder.appendChild(rr);
        });
      }
      if (lic && lic.type === 'hpt-credits') {
        fetchProduct(PRODUCTS.hptCredits).then(function (p) {
          if (!p) return;
          if (!state.creditsTouched) state.creditsQty = computedCreditsDefault();
          var ficmExtra = state.route.extras && state.route.extras.some(function (x) { return x.extraCredits && state.extraSel[x.handle]; });
          var tdef3 = engineTrans();
          var transExtra = (state.wantTrans && tdef3 && tdef3.extraCredits) ? tdef3.extraCredits : 0;
          lHolder.appendChild(el('p', 'tc-note', 'HP Tuners needs credits to license a new vehicle. We\'ve preset the amount your tune requires' + (ficmExtra ? ' (includes 2 extra credits for the FICM module)' : '') + (transExtra ? ' (includes ' + transExtra + ' extra credits for the Allison TCM)' : '') + ' — we\'ll confirm before tuning.'));
          var row = el('div', 'tc-qty-row', '<span>' + p.title + ' — <strong>' + money(p.variants[0].price) + '</strong> each</span>');
          var qty = document.createElement('input');
          qty.type = 'number'; qty.min = '0'; qty.max = '16'; qty.value = state.creditsQty; qty.className = 'tc-qty';
          qty.addEventListener('change', function () { state.creditsTouched = true; state.creditsQty = Math.max(0, Math.min(16, parseInt(qty.value || '0', 10))); });
          row.appendChild(qty);
          lHolder.appendChild(row);
        });
      }
    }

    if (!any) bodyEl.appendChild(el('p', 'tc-note', 'No extra options needed for your setup — you\'re good to continue.'));
  }

  // Contact info is collected at checkout — the order form only asks for
  // truck details relevant to what's actually in the build.
  /* ============================= VIN CHECK + DECODE ============================= */
  // Brent 2026-10-12: the VIN is checked as it's typed (17 characters, no I / O / Q, position-9 check digit)
  // and a clean VIN is decoded through the NHTSA vPIC service to pre-fill Year / Make / Model. The lookup
  // never blocks the order — if it fails or is slow, the customer fills in the truck by hand.
  var VIN_TRANSLIT = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 };
  var VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
  var VIN_DECODE_URL = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/';
  var PLATFORM_MAKES = { powerstroke: ['FORD'], duramax: ['CHEVROLET', 'GMC'], cummins: ['RAM', 'DODGE'], ecodiesel: ['RAM', 'JEEP', 'DODGE'] };
  var DIESEL_FAMILY = { FORD: 'Powerstroke', CHEVROLET: 'Duramax', GMC: 'Duramax', RAM: 'Cummins', DODGE: 'Cummins' };

  function cleanVin(v) { return String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }

  function vinCheckDigitOk(vin) {
    var sum = 0;
    for (var i = 0; i < 17; i++) {
      var c = vin.charAt(i);
      var n = /[0-9]/.test(c) ? Number(c) : VIN_TRANSLIT[c];
      if (n === undefined) return false;
      sum += n * VIN_WEIGHTS[i];
    }
    var rem = sum % 11;
    return vin.charAt(8) === (rem === 10 ? 'X' : String(rem));
  }

  // '' when the VIN is well-formed, otherwise a short message for the field
  function vinProblem(vin) {
    if (!vin) return '';
    if (vin.length !== 17) return 'A VIN is 17 characters — this one has ' + vin.length + '.';
    if (/[IOQ]/.test(vin)) return 'VINs never use the letters I, O or Q — those are usually a 1 or a 0.';
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) return 'That VIN has a character we don\'t recognise — check it against the door jamb sticker.';
    if (!vinCheckDigitOk(vin)) return 'That VIN doesn\'t pass its check digit — one character is probably mistyped.';
    return '';
  }

  // Brent 2026-10-12: SCT device serial numbers (X4 / BDX / Livewire) are exactly 13 characters
  function sctSerialProblem(serial) {
    var s = String(serial || '').replace(/\s+/g, '');
    if (!s) return '';
    return s.length === 13 ? '' : 'SCT device serial numbers are exactly 13 characters — this one has ' + s.length + '.';
  }

  function titleMake(make) {
    if (!make) return '';
    return make === 'GMC' ? make : make.charAt(0) + make.slice(1).toLowerCase();
  }

  function engineYears() {
    var m = state.engine && /(\d{4})(?:\.5)?\s*[–-]\s*(\d{4})/.exec(state.engine.label);
    return m ? [Number(m[1]), Number(m[2])] : null;
  }

  // Soft warning when the decoded truck doesn't line up with the platform / engine picked in step 1
  function vinMismatch(d) {
    if (!d || !state.platform) return '';
    var makes = PLATFORM_MAKES[state.platform] || [];
    var yrs = engineYears();
    var makeOk = !d.make || !makes.length || makes.indexOf(d.make) >= 0;
    var yearOk = !d.year || !yrs || (d.year >= yrs[0] && d.year <= yrs[1]);
    if (makeOk && yearOk) return '';
    var platformLabel = ((CATALOG[state.platform] && CATALOG[state.platform].label) || state.platform).replace(/\s*\(.*\)$/, '');
    var picked = (state.engine ? state.engine.label + ' ' : '') + platformLabel;
    return 'Heads up: this VIN decodes to a ' + d.text + ', but you picked ' + picked + '. Double-check the truck step before you order.';
  }

  function vinDecodedText(r) {
    var make = String(r.Make || '').toUpperCase();
    var parts = [r.ModelYear, titleMake(make), r.Model].filter(Boolean);
    var litres = Number(r.DisplacementL);
    if (litres) parts.push(String(Math.round(litres * 10) / 10) + 'L');
    if (/diesel/i.test(r.FuelTypePrimary || '') && DIESEL_FAMILY[make]) parts.push(DIESEL_FAMILY[make]);
    return parts.join(' ');
  }

  function setVinStatus(status, text, warn) {
    state.vin.status = status; state.vin.text = text || ''; state.vin.warn = warn || '';
    paintVinStatus();
  }

  function paintVinStatus() {
    var st = document.getElementById('tc-vin-status');
    if (st) {
      st.textContent = state.vin.text;
      st.className = 'tc-hint tc-vin-status' + (state.vin.status ? ' tc-vin-status--' + state.vin.status : '');
      st.hidden = !state.vin.text;
    }
    var wn = document.getElementById('tc-vin-warn');
    if (wn) { wn.textContent = state.vin.warn; wn.hidden = !state.vin.warn; }
  }

  // Year / Make / Model is read-only and always mirrors the decoded VIN (blank until a VIN decodes)
  function setYearModel(text) {
    state.form.yearmodel = text || ''; state.vin.auto = text || '';
    var input = document.getElementById('tc-yearmodel');
    if (input) input.value = state.form.yearmodel;
  }

  function applyVinDecode(d) { setYearModel(d.text); }

  function lookupVin(vin) {
    var cached = state.vin.decoded;
    if (cached && cached.vin === vin) { applyVinDecode(cached); setVinStatus('ok', 'Decoded: ' + cached.text, vinMismatch(cached)); return; }
    if (typeof window.fetch !== 'function') { setYearModel(''); setVinStatus('ok', 'VIN looks good.', ''); return; }
    var seq = ++state.vin.seq;
    setVinStatus('busy', 'Checking VIN…', '');
    window.fetch(VIN_DECODE_URL + encodeURIComponent(vin) + '?format=json')
      .then(function (res) { return res.ok ? res.json() : Promise.reject(new Error('HTTP ' + res.status)); })
      .then(function (json) {
        if (seq !== state.vin.seq) return;
        var r = (json && json.Results && json.Results[0]) || {};
        var year = Number(r.ModelYear) || 0;
        if (!r.Make || !year) { setYearModel(''); setVinStatus('warn', 'VIN looks valid, but we couldn\'t decode the truck from it — we\'ll confirm it from your VIN after you order.', ''); return; }
        var d = { vin: vin, year: year, make: String(r.Make).toUpperCase(), model: r.Model || '', text: vinDecodedText(r) };
        state.vin.decoded = d;
        applyVinDecode(d);
        setVinStatus('ok', 'Decoded: ' + d.text, vinMismatch(d));
      })
      .catch(function () { if (seq === state.vin.seq) { setYearModel(''); setVinStatus('warn', 'VIN looks valid. We couldn\'t look it up right now — we\'ll confirm your truck from the VIN after you order.', ''); } });
  }

  // Live feedback while typing (VIN input handler)
  function onVinInput(input, wrap) {
    var vin = cleanVin(input.value);
    if (input.value !== vin) input.value = vin;
    state.form.vin = vin;
    state.vin.seq++;
    wrap.classList.remove('tc-field--error');
    if (!vin) { setYearModel(''); setVinStatus('', '', ''); return; }
    var problem = vinProblem(vin);
    if (!problem) { lookupVin(vin); return; }
    setYearModel('');
    if (vin.length >= 17 || /[IOQ]/.test(vin)) { wrap.classList.add('tc-field--error'); setVinStatus('error', problem, ''); }
    else setVinStatus('', vin.length + ' of 17 characters', '');
  }

  // the camera tip only shows where the scanner button does (phones and tablets, assets/pk-vin-scan.js)
  function vinHint() {
    var scan = window.PkVinScan && typeof window.PkVinScan.available === 'function' && window.PkVinScan.available();
    return 'Found on the driver\'s door jamb or dash.' + (scan ? ' Tap the camera to scan the door jamb barcode.' : '') + ' We check it and fill in your truck below.';
  }

  function formFields() {
    var r = state.route;
    var fields = [
      { id: 'vin', label: 'VIN (17 characters)', type: 'text', required: true, validate: vinProblem, hint: vinHint() },
      { id: 'yearmodel', label: 'Year / Make / Model', type: 'text', required: false, readonly: true, placeholder: 'Filled in from your VIN', hint: 'Filled in automatically once your VIN checks out — nothing to type here.' }
    ];
    // product-specific requirements
    var ecmTagExcluded = r && r.ecmTagExclude && state.tuneVariant && r.ecmTagExclude.test(state.tuneVariant.title);
    if (r && r.requiresEcmTag && !ecmTagExcluded) {
      fields.push(
        { id: 'ecmEc', label: 'E/C number (from the ECM tag)', type: 'text', required: true, hint: 'On the sticker on your truck\'s ECM.' },
        { id: 'ecmSn', label: 'SN number (from the ECM tag)', type: 'text', required: true, hint: 'On the sticker on your truck\'s ECM.' }
      );
    } else if (r && r.requiresEcmEc) {
      // Brent 2026-10-12: 22-24 Cummins EZ Lynk files are sorted by the E/C number, so it is required
      fields.push({ id: 'ecmEc', label: 'E/C number (from the ECM sticker)', type: 'text', required: true, hint: 'On the sticker on your truck\'s ECM — your files are built from this number.' });
    }
    if (r && r.deviceFamily === 'hptuners' && state.hasDevice === true) {
      fields.push({ id: 'deviceSerial', label: 'HP Tuners device serial number', type: 'text', required: true, hint: 'Found in VCM Suite or on the device label.' });
    }
    if (r && r.deviceFamily === 'sct' && state.hasDevice === true) {
      fields.push({ id: 'deviceSerial', label: 'SCT device serial number (X4 / BDX / Livewire)', type: 'text', required: true, validate: sctSerialProblem, hint: 'Found in the device info menu or on the label — exactly 13 characters.' });
    }
    if (r && r.orderFields) {
      r.orderFields.forEach(function (f) {
        if (f.ownedOnly && state.hasDevice !== true) return;
        fields.push({ id: f.id, label: f.label, type: 'text', required: true, hint: f.hint });
      });
    }
    var lic = licenseNeed();
    if (lic && lic.type === 'efilive-vin') {
      fields.push(
        { id: 'efiSerial', label: 'EFI Live device serial number', type: 'text', required: true, hint: 'EFI Live needs this to issue your VIN license.' },
        { id: 'efiAuth', label: 'EFI Live auth code', type: 'text', required: true, hint: 'Found in the EFI Live V8 software under your device info.' }
      );
    }
    // link codes for an owned AutoCal: we need the device serial number even when no VIN license is required
    else if (r && r.deviceFamily === 'efilive' && state.linkCodes === 'need') {
      fields.push({ id: 'efiSerial', label: 'EFI Live device serial number', type: 'text', required: true, hint: 'We need your AutoCal serial number to generate your link codes.' });
    }
    if (r && r.tune.powerLevelsText && !state.sotf) {
      fields.push({ id: 'powerRequest', label: 'Requested power level(s) / tune type', type: 'text', required: true, placeholder: 'See the tune options listed on the product page' });
    }
    if (r && r.extras) {
      r.extras.forEach(function (x) {
        if (x.powerLevels && state.extraSel[x.handle]) {
          fields.push({ id: 'ficmLevels', label: 'FICM power level(s)', type: 'text', required: true, placeholder: x.powerLevels.join(' / '), hint: 'Pick one per FICM file ordered; note induction heating preference in Notes.' });
        }
      });
    }
    if (state.wantTrans || engineTrans()) {
      fields.push({ id: 'transmission', label: 'Transmission', type: 'text', required: !!state.wantTrans, placeholder: 'e.g. 68RFE, Allison 1000, 6R140, manual' });
    }
    fields.push(
      { id: 'tires', label: 'Tire size (if not stock)', type: 'text', required: false },
      { id: 'mods', label: 'Current mods (injectors, turbo, lift pump, exhaust…)', type: 'textarea', required: false },
      { id: 'notes', label: 'Anything else we should know?', type: 'textarea', required: false }
    );
    return fields;
  }

  function renderOrder() {
    bodyEl.appendChild(el('h3', 'tc-h', 'Tell us about the truck'));
    bodyEl.appendChild(el('p', 'tc-note', 'This info rides along with your order so we can build your file right the first time. Your contact details are collected at checkout.'));
    if (state.route && state.route.deviceFamily === 'hptuners') {
      bodyEl.appendChild(el('p', 'tc-note', 'After ordering: MPVI2/MPVI3 users — email your stock read to sales@peaksideperformance.ca with your order number. RTD4 users — watch your email for our tuner invite code.'));
    }
    var form = el('div', 'tc-form');
    bodyEl.appendChild(form);
    formFields().forEach(function (f) {
      var w = el('div', 'tc-field');
      w.innerHTML = '<label for="tc-' + f.id + '">' + f.label + (f.required ? ' <span class="tc-req">*</span>' : '') + '</label>';
      var input = document.createElement(f.type === 'textarea' ? 'textarea' : 'input');
      if (f.type !== 'textarea') input.type = f.type;
      input.id = 'tc-' + f.id;
      if (f.placeholder) input.placeholder = f.placeholder;
      input.value = state.form[f.id] || '';
      input.addEventListener('input', function () { state.form[f.id] = input.value.trim(); w.classList.remove('tc-field--error'); });
      if (f.id === 'vin') {
        input.autocomplete = 'off'; input.spellcheck = false;
        input.setAttribute('autocapitalize', 'characters');
        input.addEventListener('input', function () { onVinInput(input, w); });
      }
      if (f.readonly) { input.readOnly = true; input.tabIndex = -1; input.setAttribute('aria-readonly', 'true'); w.classList.add('tc-field--readonly'); }
      w.appendChild(input);
      // Brent 2026-10-13: camera button beside the VIN box scans the door jamb barcode (assets/pk-vin-scan.js)
      if (f.id === 'vin' && window.PkVinScan) window.PkVinScan.attach(input);
      if (f.hint) w.appendChild(el('small', 'tc-hint', f.hint));
      if (f.id === 'vin') {
        var vinStatus = el('small', 'tc-hint tc-vin-status'); vinStatus.id = 'tc-vin-status'; vinStatus.hidden = true; w.appendChild(vinStatus);
        var vinWarn = el('small', 'tc-hint tc-vin-warn'); vinWarn.id = 'tc-vin-warn'; vinWarn.hidden = true; w.appendChild(vinWarn);
      }
      form.appendChild(w);
    });
    paintVinStatus();
  }

  function renderReview() {
    bodyEl.appendChild(el('h3', 'tc-h', 'Review your build'));
    var list = el('div', 'tc-review');
    bodyEl.appendChild(list);
    summaryEl = list;
    buildCartItems().then(function (res) {
      list.innerHTML = '';
      var total = 0;
      res.display.forEach(function (d) {
        total += d.price * (d.qty || 1);
        list.appendChild(el('div', 'tc-line',
          '<span>' + d.label + (d.qty > 1 ? ' × ' + d.qty : '') + '</span><strong>' + money(d.price * (d.qty || 1)) + '</strong>'));
      });
      list.appendChild(el('div', 'tc-line tc-line--total', '<span>Estimated total</span><strong>' + money(total) + '</strong>'));
      list.appendChild(el('p', 'tc-note', 'Shipping & taxes calculated at checkout. Tune files are built after we review your order details.'));
    });
  }

  /* ============================== LOGIC ================================ */

  function resetDownstream(from) {
    if (from === 'platform' || from === 'engine') {
      state.deviceFamily = null; state.route = null; state.tuneVariant = null; state.sotf = null;
      state.switchSel = {}; state.extraSel = {}; state.powerSel = []; state.customRequest = ''; state.wantTrans = false;
      state.wantSupport = false; state.supportVariant = null; state.cables = {}; state.creditsQty = 0; state.creditsTouched = false;
      state.l5p1718 = null; state.e42Unlock = null; state.linkCodes = null;
      if (from === 'platform') state.hasDevice = null;
    }
    if (from === 'device') { state.deviceFamily = null; state.flashedBefore = null; state.route = null; state.tuneVariant = null; state.buyDevice = null; state.sotf = null; state.switchSel = {}; state.extraSel = {}; state.powerSel = []; state.creditsQty = 0; state.creditsTouched = false; state.l5p1718 = null; state.linkCodes = null; }
    if (from === 'family') { state.route = null; state.tuneVariant = null; state.sotf = null; state.switchSel = {}; state.extraSel = {}; state.powerSel = []; state.creditsQty = 0; state.creditsTouched = false; state.l5p1718 = null; state.linkCodes = null; }
    if (from === 'route') { state.tuneVariant = null; state.sotf = null; state.switchSel = {}; state.extraSel = {}; state.powerSel = []; state.customRequest = ''; state.buyDevice = null; state.cables = {}; state.creditsQty = 0; state.creditsTouched = false; state.wantSupport = false; state.supportVariant = null; state.linkCodes = null; }
  }

  function availableRoutes() {
    if (!state.engine) return [];
    var routes = state.engine.routes;
    if (state.hasDevice === true && state.deviceFamily) {
      routes = routes.filter(function (r) { return r.deviceFamily === state.deviceFamily; });
    }
    return routes;
  }

  function engineTrans() {
    if (!state.engine) return null;
    if (state.engine.trans) return state.engine.trans;
    if (state.engine.transByRoute && state.route) return state.engine.transByRoute[state.route.deviceFamily] || null;
    return null;
  }

  // power-level list for the currently selected tune variant (null when not applicable)
  function powerLevelsFor() {
    var r = state.route;
    if (!r || !state.tuneVariant || state.sotf) return null;
    if (r.deviceFamily === 'ezlynk') return null;
    var t = r.tune;
    if (t.powerLevelsByMatch) {
      var band = t.powerLevelsByMatch.find(function (b) { return b.match.test(state.tuneVariant.title); });
      return band ? band.levels : null;
    }
    return t.powerLevels || null;
  }

  // how many power levels the chosen variant includes (1 for single, N for "N Files")
  function levelCount(title) {
    if (/all levels/i.test(title)) return 0;
    var m = title.match(/(\d+)\s*(?:single\s*)?files?/i);
    if (m) return parseInt(m[1], 10);
    return 1;
  }

  // credits including any add-ons that need extra credits (e.g. FICM +2)
  function computedCreditsDefault() {
    var r = state.route;
    if (!r) return 0;
    // base credits only when the vehicle still needs licensing on their device
    var total = (state.hasDevice === false || state.flashedBefore === false) ? (r.creditsDefault || 2) : 0;
    // extra modules (e.g. FICM) always need their own credits
    if (r.extras) {
      r.extras.forEach(function (x) {
        if (x.extraCredits && state.extraSel[x.handle]) total += x.extraCredits;
      });
    }
    // transmission tuning that needs its own credits (e.g. 17–23 L5P Allison TCM on HP Tuners: +4)
    var tdef = engineTrans();
    if (tdef && tdef.extraCredits && state.wantTrans) total += tdef.extraCredits;
    return total;
  }

  function licenseNeed() {
    var r = state.route;
    if (!r || !r.license) return null;
    if (r.license === 'efilive-vin') {
      // a brand-new AutoCal ships with one VIN license included, so only an
      // owned device that has never flashed this truck needs a new license
      if (state.hasDevice === true && state.flashedBefore === false) return { type: r.license };
      return null;
    }
    // HP Tuners credits: new device, owned device that hasn't licensed this truck,
    // or an extra module (e.g. FICM) that needs its own credits
    var extraNeedsCredits = r.extras && r.extras.some(function (x) { return x.extraCredits && state.extraSel[x.handle]; });
    var tdef2 = engineTrans();
    var transNeedsCredits = !!(state.wantTrans && tdef2 && tdef2.extraCredits);
    var needed = state.hasDevice === false || state.flashedBefore === false || extraNeedsCredits || transNeedsCredits;
    if (!needed) return null;
    return { type: r.license };
  }

  function validate(stepId) {
    if (stepId === 'truck') return !!(state.platform && state.engine) || 'Pick your platform and engine to continue.';
    if (stepId === 'device') {
      if (state.hasDevice === null) return 'Let us know if you have a device.';
      if (state.hasDevice === true && !state.deviceFamily) return 'Pick which device you have.';
      return true;
    }
    if (stepId === 'flashed') return state.flashedBefore !== null || 'Let us know if this device has flashed this truck.';
    if (stepId === 'route') {
      if (!state.route) return 'Pick a tuning package.';
      if (state.route.gate1718 && (state.hasDevice === false || state.flashedBefore === false) && state.l5p1718 === null) return 'Let us know if your truck is a 2017 or 2018.';
      if (!state.tuneVariant) return 'Pick a tune option.';
      var lv = powerLevelsFor();
      if (lv) {
        var need = levelCount(state.tuneVariant.title);
        if (need > 0 && state.powerSel.length !== need) {
          return need === 1 ? 'Pick the power level you\'d like.' : 'Pick exactly ' + need + ' power levels (' + state.powerSel.length + ' selected).';
        }
        var customPicked = lv.some(function (l) {
          if (typeof l === 'string' || !l.custom) return false;
          var key = l.label + (l.hp ? ' (' + l.hp + ')' : '');
          return state.powerSel.indexOf(key) >= 0;
        });
        if (customPicked && !(state.customRequest || '').trim()) {
          var box = document.querySelector('.tc-field--custom');
          if (box) box.classList.add('tc-field--error');
          return 'Tell us about your custom request so we can build the right file.';
        }
      }
      if (state.route.e42Gate) {
        if (!state.e42Unlock) return 'Let us know if your ECM has been unlocked.';
        if (state.e42Unlock === 'no') return 'Your ECM needs to be unlocked first — order the GM E42 ECM Service from the link above, then choose "It\'s currently being unlocked" to continue.';
      }
      if (state.hasDevice === false && state.route.devices.length && !state.buyDevice) return 'Pick your device (or go back and tell us you have one).';
      return true;
    }
    if (stepId === 'options') {
      var needsLinkCodes = state.route && state.route.deviceFamily === 'efilive' && state.hasDevice === true;
      if (needsLinkCodes && !state.linkCodes) return 'Let us know if you need link codes for your AutoCal.';
      return true;
    }
    if (stepId === 'order') {
      var bad = null, badMsg = '';
      formFields().forEach(function (f) {
        var v = state.form[f.id] || '';
        var fieldEl = document.getElementById('tc-' + f.id);
        var wrap = fieldEl && (fieldEl.closest('.tc-field') || fieldEl.parentElement);
        var problem = (f.validate && v) ? f.validate(v) : '';
        var ok = (!f.required || v.length > 0) && (!f.pattern || !v || f.pattern.test(v)) && !problem;
        if (!ok && wrap) { wrap.classList.add('tc-field--error'); if (!bad) { bad = f; badMsg = problem; } }
      });
      return bad ? ('Please check the "' + bad.label + '" field.' + (badMsg ? ' ' + badMsg : '')) : true;
    }
    return true;
  }

  function buildCartItems() {
    var props = {
      'VIN': state.form.vin || '',
      'Truck': state.form.yearmodel || '',
      'Platform': CATALOG[state.platform].label + ' — ' + state.engine.label,
      'Device': state.hasDevice ? ('Customer-owned ' + (DEVICE_FAMILY_LABELS[state.deviceFamily] || '')) : (state.buyDevice ? state.buyDevice.title : 'None'),
      'Flashed this truck before': state.hasDevice ? (state.flashedBefore ? 'Yes' : 'No') : 'New device',
      'Power levels': state.powerSel.join(', '),
      'Custom request': state.customRequest || '',
      'Requested power level(s)': state.form.powerRequest || '',
      'FICM power level(s)': state.form.ficmLevels || '',
      'ECM tag E/C': state.form.ecmEc || '',
      'ECM tag SN': state.form.ecmSn || '',
      'Device serial number': state.form.deviceSerial || '',
      'EFI Live serial number': state.form.efiSerial || '',
      'EFI Live auth code': state.form.efiAuth || '',
      'Transmission': state.form.transmission || '',
      'Tire size': state.form.tires || '',
      'Mods': state.form.mods || '',
      'Notes': state.form.notes || '',
      'ECM unlock status': state.e42Unlock === 'yes' ? 'Unlocked' : state.e42Unlock === 'inprogress' ? 'Unlock in progress' : '',
      'AutoCal link codes': state.linkCodes === 'need' ? 'Customer needs link codes' : state.linkCodes === 'linked' ? 'Already linked / owns FlashScan' : ''
    };
    if (state.route && state.route.orderFields) {
      state.route.orderFields.forEach(function (f) { props[f.label] = state.form[f.id] || ''; });
    }
    Object.keys(props).forEach(function (k) { if (!props[k]) delete props[k]; });

    var items = [];
    var display = [];

    items.push({ id: state.tuneVariant.id, quantity: 1, properties: props });
    display.push({ label: 'Tune: ' + (state.tuneVariant.title === 'Default Title' ? state.route.label : state.tuneVariant.title), price: state.tuneVariant.price });

    var chain = Promise.resolve();

    if (state.buyDevice) {
      items.push({ id: state.buyDevice.variant.id, quantity: 1 });
      display.push({ label: 'Device: ' + state.buyDevice.title, price: state.buyDevice.variant.price });
    }
    Object.keys(state.switchSel).forEach(function (h) {
      var s = state.switchSel[h];
      items.push({ id: s.variant.id, quantity: 1 });
      display.push({ label: s.title, price: s.variant.price });
    });
    Object.keys(state.extraSel).forEach(function (h) {
      var s = state.extraSel[h];
      items.push({ id: s.variant.id, quantity: 1, properties: { 'VIN': state.form.vin || '', 'Truck': state.form.yearmodel || '' } });
      display.push({ label: s.title + (s.variant.title !== 'Default Title' ? ' — ' + s.variant.title : ''), price: s.variant.price });
    });
    if (state.wantSupport && state.supportVariant) {
      items.push({ id: state.supportVariant.id, quantity: 1 });
      display.push({ label: 'Support package: ' + state.supportVariant.title, price: state.supportVariant.price });
    }
    Object.keys(state.cables).forEach(function (h) {
      if (!state.cables[h]) return;
      chain = chain.then(function () {
        return fetchProduct(h).then(function (p) {
          if (!p) return;
          items.push({ id: p.variants[0].id, quantity: 1 });
          display.push({ label: p.title, price: p.variants[0].price });
        });
      });
    });

    var trans = engineTrans();
    if (state.wantTrans && trans) {
      chain = chain.then(function () {
        return fetchProduct(trans.handle).then(function (p) {
          if (!p) return;
          items.push({ id: p.variants[0].id, quantity: 1, properties: { 'VIN': state.form.vin || '', 'Truck': state.form.yearmodel || '' } });
          display.push({ label: trans.label || p.title, price: p.variants[0].price });
        });
      });
    }

    var lic = licenseNeed();
    if (lic) {
      chain = chain.then(function () {
        if (lic.type === 'efilive-vin') {
          return fetchProduct(PRODUCTS.efiliveLicense).then(function (p) {
            if (!p) return;
            items.push({ id: p.variants[0].id, quantity: 1, properties: { 'VIN': state.form.vin || '' } });
            display.push({ label: p.title, price: p.variants[0].price });
          });
        }
        if (lic.type === 'hpt-credits' && state.creditsQty > 0) {
          return fetchProduct(PRODUCTS.hptCredits).then(function (p) {
            if (!p) return;
            items.push({ id: p.variants[0].id, quantity: state.creditsQty });
            display.push({ label: p.title, price: p.variants[0].price, qty: state.creditsQty });
          });
        }
      });
    }

    return chain.then(function () { return { items: items, display: display }; });
  }

  function addToCart() {
    nextBtn.disabled = true;
    nextBtn.textContent = 'Adding…';
    buildCartItems().then(function (res) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: res.items })
      });
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Could not add to cart'); });
      window.location.href = '/cart';
    }).catch(function (e) {
      nextBtn.disabled = false;
      nextBtn.textContent = 'Add to Cart';
      showError(e.message || 'Something went wrong — please try again or contact us.');
    });
  }

  var errEl;
  function showError(msg) {
    if (!errEl) { errEl = el('div', 'tc-error'); bodyEl.parentElement.insertBefore(errEl, bodyEl.nextSibling); }
    errEl.textContent = msg;
    errEl.style.display = 'block';
    errEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  function hideError() { if (errEl) errEl.style.display = 'none'; }

  /* ============================== WIRE-UP ============================== */

  function init() {
    root = document.querySelector('[data-tune-configurator]');
    if (!root) return;
    stepsEl = root.querySelector('.tc-steps');
    bodyEl = root.querySelector('.tc-body');
    backBtn = root.querySelector('.tc-back');
    nextBtn = root.querySelector('.tc-next');

    backBtn.addEventListener('click', function () {
      hideError();
      if (currentStep > 0) { currentStep--; render(); root.scrollIntoView({ behavior: 'smooth' }); }
    });
    nextBtn.addEventListener('click', function () {
      hideError();
      var steps = stepList();
      var v = validate(steps[currentStep].id);
      if (v !== true) { showError(v); return; }
      if (steps[currentStep].id === 'review') { addToCart(); return; }
      currentStep++;
      render();
      root.scrollIntoView({ behavior: 'smooth' });
    });
    render();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
